import { apiClient } from "@/api/api-client";
import { Button } from "@/components/core/buttons/button";
import ImageSelect from "@/components/core/forms/image-select";
import Select from "@/components/core/forms/select";
import { useUpdateUserRoleMutation, useUserQuery, useUserSessionsQuery } from "@/features/dashboard/api/queries/user-approval-query";
import type { UserSession } from "@/features/dashboard/api/services/user-approval-service";
import { formatDate } from "@/utils/format-date";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { FC } from "react";
import { toast } from "sonner";
export const Route = createFileRoute("/_dashboard/user-approvals_/$userId")({
	component: RouteComponent,
});

interface User {
	id: string;
	email: string;
	first_name: string;
	last_name: string;
	contact_number: string;
	profile_picture?: string;
	created_at: string;
	updated_at: string;
	approval_status: string;
	last_login: string;
	is_admin: boolean;
	roles?: string[];
}

function RouteComponent() {
	const { userId } = Route.useParams();
	const userQuery = useUserQuery(userId);
	const userSessionsQuery = useUserSessionsQuery(userId);
	const [activeTab, setActiveTab] = useState<string>("details");
	const baseUrl = "http://localhost:8000";

	const user = userQuery.data || {};

	const sessions: UserSession[] = userSessionsQuery.data || [];

	const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);

	useEffect(() => {
		if (user?.profile_picture) {
			setProfilePictureUrl(baseUrl + user.profile_picture);
		}
	}, [user?.profile_picture]);

	const [profilePicture, setProfilePicture] = useState<string | null>(user?.profile_picture || null);

	const handleImageCrop = async (croppedImage: string) => {
		try {
			const response = await fetch(croppedImage);
			const blob = await response.blob();

			const formData = new FormData();
			formData.append("profile_picture", blob, "profile.png");

			const uploadResponse = await apiClient.post(`/user-approvals/${userId}/avatar`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			const data = uploadResponse.data;
			setProfilePicture(data.profile_picture_url);
			toast.success("Profile picture updated");
		} catch (error) {
			console.error("Failed to upload:", error);
			toast.error("Failed to update profile picture");
		}
	};
	const handleImageRemove = async () => {
		try {
			const response = await fetch(`/api/user-approvals/${userId}/avatar`, {
				method: "DELETE",
			});

			if (!response.ok) throw new Error("Delete failed");

			setProfilePicture(null);
			toast.success("Profile picture removed");
		} catch (error) {
			console.error("Failed to remove:", error);
			toast.error("Failed to remove profile picture");
		}
	};

	const getUserInitials = () => {
		const first = user?.first_name?.[0] || "";
		const last = user?.last_name?.[0] || "";
		return (first + last).toUpperCase() || "U";
	};

	const statusColors: Record<string, string> = {
		pending: "bg-yellow-100 text-yellow-800",
		approved: "bg-green-100 text-green-800",

		default: "bg-gray-100 text-gray-800",
	};

	const tabs = [
		{ id: "details", label: "Details" },
		{ id: "roles", label: "Roles" },
		{ id: "sessions", label: "Sessions" },
	];

	return (
		<div className="container mx-auto px-4 py-6">
			<div className="flex flex-row gap-6">
				{/* Sidebar Navigation */}
				<div className="flex flex-col w-80 flex-shrink-0">
					<div className="bg-white rounded-lg shadow">
						<nav className="flex flex-col">
							{tabs.map((tab) => (
								<button
									type="button"
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									className={`
                    flex flex-row p-3 w-full text-left transition-colors
                    ${activeTab === tab.id ? "bg-primary-50 text-black-600 border-l-4 border-primary-600" : "hover:bg-gray-50"}
                  `}
								>
									<span className="text-base font-medium">{tab.label}</span>
								</button>
							))}
						</nav>
					</div>
				</div>

				{/* Main Content Area */}
				<div className="flex flex-col flex-1">
					{activeTab === "details" && (
						<UserDetailsTab
							user={user}
							profilePictureUrl={profilePictureUrl}
							statusColors={statusColors}
							handleImageCrop={handleImageCrop}
							handleImageRemove={handleImageRemove}
							getUserInitials={getUserInitials}
						/>
					)}
					{activeTab === "roles" && <UserRolesTab user={user} />}
					{activeTab === "sessions" && <UserSessionsTab sessions={sessions} />}
				</div>
			</div>
		</div>
	);
}

// Tabs
const UserDetailsTab: FC<{
	user: User;
	statusColors: Record<string, string>;
	handleImageCrop: (croppedImage: string) => void;
	handleImageRemove: () => void;
	profilePictureUrl: string | null;
	getUserInitials: () => string;
}> = ({ user, statusColors, handleImageCrop, handleImageRemove, profilePictureUrl, getUserInitials }) => {
	return (
		<>
			<div className="mb-6">
				<h1 className="text-xl font-semibold text-foreground-dark">User Details</h1>
				<p className="text-base text-foreground/70">Manage your user profile.</p>
			</div>

			<div className="mb-8">
				<ImageSelect
					avatar={profilePictureUrl}
					initials={getUserInitials()}
					onCrop={handleImageCrop}
					onImageRemove={handleImageRemove}
					hasLabel={false}
					size={90}
				/>
			</div>

			<div className="bg-white rounded-lg shadow">
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Status</span>
					</div>
					<div className="p-4 flex-1">
						<span className={`text-sm font-medium rounded-md px-4 py-2 ${statusColors[user?.approval_status] || statusColors.default}`}>
							{user?.approval_status}
						</span>
					</div>
				</div>
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Name</span>
					</div>
					<div className="p-4 flex-1">
						<span className="text-sm">
							{user?.first_name} {user?.last_name}
						</span>
					</div>
				</div>
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Email</span>
					</div>
					<div className="p-4 flex-1">
						<span className="text-sm">{user?.email}</span>
					</div>
				</div>
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Last Login</span>
					</div>
					<div className="p-4 flex-1">
						<span className="text-sm">{formatDate(user?.last_login, "PPpp") || "-"}</span>
					</div>
				</div>
				<div className="flex flex-row">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Date Created</span>
					</div>
					<div className="p-4 flex-1">
						<span className="text-sm">{formatDate(user?.created_at, "PPpp")}</span>
					</div>
				</div>
			</div>
		</>
	);
};

const UserRolesTab: FC<{ user: User }> = ({ user }) => {
	const currentRole = user?.roles?.[0] || "";
	const [selectedRole, setSelectedRole] = useState(currentRole);
	const updateRoleMutation = useUpdateUserRoleMutation();

	useEffect(() => {
		if (user?.roles?.[0]) {
			setSelectedRole(user.roles[0]);
		}
	}, [user?.roles]);

	const roleOptions = [
		{ value: "admin", label: "Admin" },
		{ value: "salesperson", label: "Salesperson" },
		{ value: "accounting", label: "Accounting" },
	];

	const handleSave = () => {
		if (!selectedRole || selectedRole === currentRole) return;
		updateRoleMutation.mutate({ userId: user.id, role: selectedRole });
	};

	return (
		<>
			<div className="mb-6">
				<h1 className="text-xl font-semibold text-foreground-dark">User Roles</h1>
				<p className="text-base text-foreground/70">Manage user role assignment.</p>
			</div>
			<div className="bg-white rounded-lg shadow">
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Current Role</span>
					</div>
					<div className="p-4 flex-1">
						<span className="text-sm capitalize">{currentRole || "No role assigned"}</span>
					</div>
				</div>
				<div className="flex flex-row border-b">
					<div className="bg-gray-50 p-4 w-40 flex-shrink-0">
						<span className="text-sm font-medium">Change Role</span>
					</div>
					<div className="p-4 flex-1">
						<div className="flex items-center gap-3">
							<Select options={roleOptions} value={selectedRole} onValueChange={setSelectedRole} placeholder="Select a role" />
							<Button
								onClick={handleSave}
								disabled={!selectedRole || selectedRole === currentRole || updateRoleMutation.isPending}
								loading={updateRoleMutation.isPending}
								size="sm"
							>
								Save
							</Button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

const UserSessionsTab: FC<{ sessions: UserSession }> = ({ sessions }) => {
	return (
		<>
			<div className="mb-6">
				<h1 className="text-xl font-semibold text-foreground-dark">User Sessions</h1>
				<p className="text-base text-foreground/70">Active user sessions and login history.</p>
			</div>

			<div className="bg-white rounded-lg shadow overflow-hidden">
				{sessions && sessions.length > 0 ? (
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Agent</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>

								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{sessions.map((session: UserSession, index: number) => (
								<tr key={session?.id || index} className="hover:bg-gray-50">
									<td className="px-6 py-4 text-sm text-gray-900">
										<div className="max-w-xs truncate" title={session?.user_agent}>
											{session?.user_agent || "Unknown"}
										</div>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{session?.ip_address || "N/A"}</td>

									<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{session?.last_activity ? session.last_activity : "N/A"}</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<div className="text-center py-8">
						<p className="text-gray-500">No sessions found</p>
					</div>
				)}
			</div>
		</>
	);
};
